import { FC } from 'react';
import { withStyles } from '../../../utils';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import styles from './CheckboxIndicator.module.scss';
import { motion, AnimatePresence } from 'motion/react';

const cn = withStyles(styles);
type State = boolean | 'indeterminate';

type CheckboxIndicatorProps = {
  checked: State;
  disabled?: boolean;
  containerClassName?: string;
};

/**
 * Visual-only; has no interactivity on its own.
 *
 * Put an `input[type="checkbox"]` AFTER it if you want it to be a checkbox:
 * ```tsx
 * <label>
 *   <CheckboxIndicator checked={checked} />
 *   <input type="checkbox" onChange={toggleChecked} />
 * </label>
 * ```
 *
 * Wrap it in a button if you want it to be a button:
 * ```tsx
 * <button onClick={toggleChecked}>
 *   <CheckboxIndicator checked={checked} />
 * </button>
 * ```
 *
 * Or it can just be a visual representation of some other thing being activated:
 * ```tsx
 * <button onClick={toggleChecked}>Toggle It</button>
 * ...
 * <CheckboxIndicator checked={checked} />
 * ```
 */
const CheckboxIndicator: FC<CheckboxIndicatorProps> = ({ checked, disabled = false, containerClassName }) => {
  return (
    <motion.span
      className={cn(
        'checkboxIndicatorContainer',
        checked === true && 'checked',
        checked === 'indeterminate' && 'indeterminate',
        disabled && 'disabled',
        containerClassName,
      )}
    >
      <AnimatePresence mode="wait">
        {checked === true && (
          <motion.div
            key="checked"
            className={cn('checkmarkContainer')}
            initial={{
              opacity: 0,
              scale: 0,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              transition: {
                duration: 0.15,
                ease: 'backOut',
              },
            }}
            exit={{
              opacity: 0,
              scale: 0,
              transition: {
                duration: 0.15,
                ease: 'backIn',
              },
            }}
          >
            <MaterialSymbol icon="check" className={cn('checkmark')} />
          </motion.div>
        )}

        {checked === 'indeterminate' && (
          <motion.div
            key="indeterminate"
            className={cn('checkmarkContainer')}
            initial={{
              opacity: 0,
              scaleX: 0,
            }}
            animate={{
              opacity: 1,
              scaleX: 1,
              transition: {
                duration: 0.15,
                ease: 'backOut',
              },
            }}
            exit={{
              opacity: 0,
              scaleX: 0,
              transition: {
                duration: 0.15,
                ease: 'backIn',
              },
            }}
          >
            <MaterialSymbol icon="check_indeterminate_small" className={cn('checkmark')} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.span>
  );
};

export default CheckboxIndicator;
